import * as React from "react";
import { WalletConfig, WalletField } from "@/types/DocumentTemplate";
import type { Event } from "@/types/Event";
import { IconBarcode } from "@tabler/icons-react";
import { QRCodeSVG } from "qrcode.react";
import { useTranslations } from "next-intl";
import {
  buildEventVariables,
  EVENT_VARIABLE_KEYS,
} from "@/lib/utils/event-variables";
import { resolveLangValue } from "@/lib/utils/resolve-lang";
import {
  brandLineTemplate,
  capBrandLine,
  rendererDropsRow,
  passColorOrNull,
} from "../passRenderRules";
import type { FieldGroup, RowCertainty } from "../passRenderRules";
import {
  dateStyleDrawsSomething,
  getFieldGroupMax,
  getSchema,
  getVisibleFieldCounts,
  isSingleDateToken,
} from "../walletPassTypeSchemas";

export type PreviewColors = {
  bg: string;
  fg: string;
  lbl: string;
};

/**
 * A colour the renderer will REJECT has to fall back here exactly as it falls
 * back on the device: `hexToRgb` returns null, `putIfPresent` omits the key,
 * and Apple applies its own default. Passing the stored string straight into a
 * CSS `style` accepted far more than PassKit does — `rgba(...)` and the literal
 * `transparent` are both reachable from the colour control this panel used to
 * share with the email builder, and a `transparent` foreground drew a preview
 * whose every value was invisible beside a perfectly readable delivered pass.
 *
 * The three fallbacks below are unchanged and remain an ASSUMPTION about what
 * Apple draws for an absent key — worth confirming on a device with a pass that
 * carries none of the three. What is now certain is that an invalid value and
 * an absent one behave the same here, which is what the renderer guarantees.
 */
export function useColors(config: WalletConfig): PreviewColors {
  return {
    bg: passColorOrNull(config.backgroundColor) ?? "#ffffff",
    fg: passColorOrNull(config.foregroundColor) ?? "#000000",
    lbl: passColorOrNull(config.labelColor) ?? "#666666",
  };
}

// Render a pass string with `{{variable}}` tokens shown as tinted inline chips.
// Per-guest substitution happens later server-side; here each token reads as an
// intentional merge-field. The chip tints from `currentColor`, so it adapts to
// whatever foreground color the pass uses (works on light or dark passes).
//
// The chip carries the TOKEN, not a sample of what it will hold — see the long
// note in `renderTemplate`, which owns that decision and its cost. Two earlier
// sentences lived here and said the opposite — that chips replaced `raw
// {{firstName}} text`, and that a dotted form showed `just its last segment`.
// The dotted form now reads `{{…foo}}`: the namespace is elided, not the name.
/**
 * Realistic stand-ins for the GUEST-level variables a pass uses.
 *
 * Length is the only quantity an operator can get wrong on a wallet pass —
 * Apple owns the font and the sizes — so these are chosen long rather than
 * tidy: `{{additionalFields.company}}` ships "REICHERT+ COMMUNICATIONS", and
 * those 24 characters are what collided with the date on a customer's device.
 * A preview that flatters is worse than none.
 *
 * WHERE THEY ARE STILL DRAWN, since the design lane no longer shows them: the
 * 1:1 view ("what the device draws"), which is the view a length judgement
 * belongs in; `resolve(...)`, which every render rule and the header-collision
 * model measure; and `useWalletSamples` consumers that ask what a field will
 * actually read. The design lane draws `{{token}}` chips instead — see
 * `renderTemplate`, which carries the trade-off that decision makes.
 *
 * EVENT-level tokens are deliberately absent: they are not guesses. The
 * builder knows its event, so `{{eventName}}` / `{{eventDate}}` / `{{eventTime}}`
 * / `{{eventLocation}}` come from {@link buildEventVariables} — the same
 * function, and therefore the same formatting, the real send uses. Hard-coding
 * them here is what put "18:00" in the preview while the pass shipped
 * "18:00 Uhr", and a German date on an English pass.
 */
const GUEST_SAMPLE_VALUES: Record<string, string> = {
  firstName: "Alexandra",
  lastName: "Weber-Hofmann",
  company: "REICHERT+ COMMUNICATIONS",
  jobTitle: "Head of Communications",
  email: "alexandra.weber@example.com",
  organizationName: "REICHERT+ COMMUNICATIONS",
  seatNumber: "14A",
  seatRow: "3",
  // seats.io Teil 2, Zug 3: the piece's block and its area.
  seatBlock: "Block C",
  seatArea: "Parkett",
  seatLabel: "Reihe 3, Platz 14",
  tableNumber: "Tisch 7",
  roomName: "Spiegelsaal",
  guestId: "68a1f3c9e4b2a71d5c3f9012",
  scanToken: "68a1f3c9e4b2a71d5c3f9012.…",
};

/**
 * The stand-in event for surfaces that have none — the template builder, the
 * preset gallery, the create wizard's card. Run through the SAME
 * {@link buildEventVariables} as a real one, so even the sample path gets the
 * locale's clock word ("18:00 Uhr") and date order right instead of the frozen
 * German strings this used to carry.
 */
const SAMPLE_EVENT = {
  eventId: "sample",
  name: "Sommerfest 2026",
  // Deliberately 24 characters: the header-collision model is calibrated
  // against exactly this length, and `{{companyName}}` is 15 in the template.
  companyName: "REICHERT+ COMMUNICATIONS",
  globalGuestLimit: 250,
  locationName: "Hotel de Rome",
  address: "Behrenstraße 37",
  zipCode: "10117",
  city: "Berlin",
  country: "Deutschland",
  timeZone: "Europe/Berlin",
  start: new Date(Date.UTC(2026, 6, 2, 16, 0, 0)),
  end: new Date(Date.UTC(2026, 6, 2, 21, 0, 0)),
} as unknown as Event;

export type WalletSamples = {
  /** token name → the text that will ship. */
  values: Record<string, string>;
  /**
   * The instant behind a date-styled field, for the Intl preview. Must be the
   * same moment the `{{eventDate}}` sample shows, or a field switching to
   * device formatting appears to jump to another day.
   */
  instant: Date | null;
  /** The same, for `{{eventEndDate}}` / `{{eventEndTime}}` — which the pass
   *  builder hands the event's END, not its start. */
  endInstant: Date | null;
  /**
   * The zone those instants must be DRAWN in. The renderer hands Apple the
   * instant with `ignoresTimeZone: true`, so the device shows the event's wall
   * clock everywhere on earth. Formatting without it used the operator's
   * browser zone and moved the date by a whole day for anyone outside the
   * event's — while the substituted `{{eventDate}}` two lines away, which comes
   * from `buildEventVariables`, was already correct.
   */
  timeZone?: string;
  /** The template's primary language — the renderer's fallback when a field
   *  carries no text under the pass language. */
  primaryLang: string;
  /**
   * The event-level half of `values`, and whether it came from a REAL event.
   *
   * These two together are the only thing the design lane can decide. An event
   * token absent from `eventValues` while `eventLoaded` is true means the event
   * carries no such value — the send will find the same emptiness and the
   * renderer will drop the row, so the preview must not draw one. Without a
   * real event the stand-in fills every token and nothing may be concluded.
   */
  eventValues: Record<string, string>;
  eventLoaded: boolean;
};

/** Sample set for `event` (its real values) or for the stand-in when null. */
export function buildWalletSamples(
  event: Event | null | undefined,
  language: string,
  eventId?: string,
  primaryLang?: string,
): WalletSamples {
  const source = event ?? SAMPLE_EVENT;
  const eventValues = buildEventVariables(source, { language, eventId });
  return {
    eventValues,
    eventLoaded: !!event,
    values: {
      ...GUEST_SAMPLE_VALUES,
      // ONE event, never a blend. Layering the stand-in underneath a real event
      // filled every token the real one could not supply — a date for an event
      // with no end, "Berlin" for one with no city, another customer's company
      // name on every preview — and the renderer drops each of those rows. The
      // bare token name is the honest fallback: it reads as a placeholder,
      // "Behrenstraße 37, 10117 Berlin" reads as data.
      ...eventValues,
    },
    // NULL when the REAL event has no such instant. Substituting the stand-in's
    // would draw a confident 2 July under a field the renderer drops for an
    // empty value — a date invented for a row the pass does not carry.
    instant: source.start ? new Date(source.start) : null,
    endInstant: source.end ? new Date(source.end) : null,
    // The real event's zone or none — borrowing the stand-in's would draw a
    // wall clock from a different continent's event.
    timeZone: source.timeZone,
    primaryLang: primaryLang || language,
  };
}

const WalletSamplesContext = React.createContext<WalletSamples | null>(null);

export function WalletSamplesProvider({
  samples,
  children,
}: {
  samples: WalletSamples;
  children: React.ReactNode;
}) {
  return (
    <WalletSamplesContext.Provider value={samples}>
      {children}
    </WalletSamplesContext.Provider>
  );
}

/**
 * The sample set for the pass being drawn.
 *
 * `WalletPreview` always installs a provider, so the fallback below only covers
 * a preview bit rendered on its own (a test mounting `FieldRow` directly). It
 * is the stand-in event, never blank — a preview that silently loses its
 * substitutions would look like the bug this whole module exists to remove.
 */
export function useWalletSamples(): WalletSamples {
  const ctx = React.useContext(WalletSamplesContext);
  const fallback = React.useMemo(() => buildWalletSamples(null, "de-DE"), []);
  return ctx ?? fallback;
}

/**
 * Whether the card is drawing ONLY what the device draws.
 *
 * Off (the default) is the builder's view: merge-field chips, an em-dash where
 * a value is missing, grey boxes naming the empty image slots, a dimmed row for
 * a field past the device's cap, and the header-collision warning. Every one of
 * those is useful while authoring and none of them exists on a pass.
 *
 * On is the fidelity view. It answers a different question — "is this what my
 * guest gets" — and it can only answer it by drawing nothing the guest would
 * not see. Suppressed rather than deleted, because the two views are both true
 * and an operator needs both: the warnings are the only thing that tells them
 * the header row overflows or that a field is past the cap.
 *
 * A context rather than a prop for the same reason `WalletSamplesProvider` is
 * one: the alternative threads a boolean through `WalletPreview`, `PassBody`,
 * six pass-type components and every shared primitive, and a lane that forgets
 * to pass it on is invisible until someone photographs it. `false` when no
 * provider is mounted, so a primitive rendered on its own in a test keeps the
 * builder behaviour it has today.
 *
 * NOT `preSubstituted`, which looks like this flag and is not: it never reaches
 * the drawing components at all (it only switches off the event-token drop
 * rule in `WalletPreview`), and the absence of chips in its lanes is a
 * consequence of the data already being substituted, not a mode.
 */
const DeviceOnlyContext = React.createContext(false);

export function DeviceOnlyProvider({
  deviceOnly,
  children,
}: {
  deviceOnly: boolean;
  children: React.ReactNode;
}) {
  return (
    <DeviceOnlyContext.Provider value={deviceOnly}>
      {children}
    </DeviceOnlyContext.Provider>
  );
}

export function useDeviceOnly(): boolean {
  return React.useContext(DeviceOnlyContext);
}

/**
 * What the delivered pass will do with the rows this client could not decide.
 *
 * `classifyUncertainRows` answers that question for the two guest lanes; this
 * carries the answer down to the components that draw. Without it a row whose
 * value the client cannot resolve is indistinguishable from a deliberate
 * label-only spacer — `FieldRow` derives both the raw and the resolved value
 * from the one already-substituted config it is handed, so `rendererDropsRow`'s
 * "configured, but resolved to nothing" arm cannot fire — and the 1:1 view drew
 * "TABLE" over a blank line while the caption beneath said the server fills it.
 *
 * A context for the same reason `DeviceOnlyContext` is one: the alternative
 * threads another prop through `WalletPreview`, `PassBody`, six pass-type
 * components and every shared primitive.
 *
 * EMPTY by default, so a primitive rendered on its own — and every lane that
 * does not run the guest rule — keeps the behaviour it has today. Nothing below
 * fires unless a caller passes a real map.
 *
 * NO PLATFORM HERE, and that is load-bearing. An earlier version keyed the
 * answer on Apple vs Google; `classifyUncertainRows` carries the measurements
 * showing that both renderers fill and drop these rows by the same rule.
 */
const PassCertaintyContext = React.createContext<
  Partial<Record<FieldGroup, Record<string, RowCertainty>>>
>({});

export function PassCertaintyProvider({
  rows,
  children,
}: {
  rows?: Partial<Record<FieldGroup, Record<string, RowCertainty>>>;
  children: React.ReactNode;
}) {
  const value = React.useMemo(() => rows ?? {}, [rows]);
  return (
    <PassCertaintyContext.Provider value={value}>
      {children}
    </PassCertaintyContext.Provider>
  );
}

/**
 * The verdict lookup for ONE group, as a callback a drawing component can ask
 * per row.
 *
 * A callback bound to a group rather than a flat key set, and the reason is not
 * theoretical: `nextFieldKey` (`WalletFieldEditor`) numbers within a group, so
 * `field_1` in `headerFields` AND in `backFields` is the ORDINARY shape of a
 * pass built in this editor — `WalletEditorBody`'s own `mapFields` comment says
 * so. A flat set let a row uncertain in one group answer for a perfectly good
 * row in another, and on the hide path that DELETED it.
 *
 * `GooglePassPreview` flattens all five groups into one `textModulesData` list
 * and therefore builds its own callback over `{field, group}` pairs — the group
 * is still known there, it is just no longer in the array's shape.
 */
export function useRowCertainty(
  group: FieldGroup,
): (field: WalletField) => RowCertainty | undefined {
  const rows = React.useContext(PassCertaintyContext);
  return React.useCallback(
    (field: WalletField) => (field.key ? rows[group]?.[field.key] : undefined),
    [rows, group],
  );
}

/** Read the whole map — for a lane that draws more than one group at once. */
export function usePassCertaintyRows(): Partial<
  Record<FieldGroup, Record<string, RowCertainty>>
> {
  return React.useContext(PassCertaintyContext);
}

/**
 * "A value lands here and this screen cannot name it."
 *
 * Coloured from the PASS palette, not from an app token. The bar sits on the
 * customer's own `backgroundColor`, so an app-theme grey is a coin flip: the
 * first version used `surface-tertiary`, which is `#f0f0f0` in the light theme
 * and `#333333` in the dark one, and was therefore invisible on a white pass in
 * a light cockpit and on a black pass in a dark one — two of four combinations,
 * including the default (`useColors` falls back to `#ffffff`). The merge-field
 * chip two hundred lines down already states the rule: tint from the pass's own
 * colours and it works on light and dark passes alike.
 *
 * Deliberately renders NO text — text would land in `deviceOnly.test.tsx`'s
 * em-dash counts — and sets a plain `backgroundColor` rather than a `color-mix`,
 * because that file identifies merge-field chips BY a `color-mix` background.
 * Sized in `em` so it tracks whatever type size the lane drawing it has set.
 */
function UnknownValueBar({ color }: { color: string }) {
  return (
    <span
      aria-hidden
      data-testid="wallet-unknown-value"
      className="inline-block h-[0.7em] w-10 max-w-full rounded-sm align-middle"
      // 0.7, and the number is measured rather than chosen. At 0.35 the bar
      // blended to 1.66:1 against a default white pass and 1.95:1 against the
      // black-and-gold one this was reported on — visible only if you knew to
      // look, which is the same failure as the app-theme grey it replaced, just
      // milder. 0.7 clears 3:1 (the threshold for a non-text graphic) in every
      // realistic pass palette measured: 3.03 default, 4.43 black/gold, 4.35 a
      // light preset, 8.85 white-on-navy.
      //
      // One case no opacity can rescue: `labelColor === backgroundColor`. There
      // the pass's own labels are invisible too, so the card is broken for a
      // reason that has nothing to do with this bar.
      style={{ backgroundColor: color, opacity: 0.7 }}
    />
  );
}

/**
 * Returned instead of a node when the row must not be drawn at all.
 *
 * A `Symbol`, not the string `"hide"`. A string IS a valid `ReactNode`, so the
 * compiler offered nothing against a call site that forgot the guard — and a
 * forgotten guard renders the literal word into the pass card. There are three
 * guards today (`FieldRow`'s filter, the strip overlay, `primaryMark` in the
 * three hand-rolled primary lanes); with a symbol, forgetting one is a type
 * error instead of a customer-visible one.
 */
export const HIDE_ROW: unique symbol = Symbol("hideRow");

/** What a row with no resolved value draws, or {@link HIDE_ROW}. */
export type EmptyValueMark = React.ReactNode | typeof HIDE_ROW;

/**
 * The one rule for "this row resolved to nothing", in one place.
 *
 * It stood copied five times — `FieldRow`, the strip overlay, and the primary
 * value of the boarding pass, store card and generic previews — as
 * `deviceOnly ? null : "—"`, which is why the undecidable case could not be
 * added without adding it five times.
 *
 * Four answers:
 *
 *  - BUILDER view: the em-dash, unchanged. It is this view's mark for "nothing
 *    here yet", and every count in `deviceOnly.test.tsx` is written against it.
 *  - DEVICE view, no verdict: nothing. Either the lane passes no map at all, or
 *    the row is a deliberate spacer the renderer keeps — the cross-repo parity
 *    fixture pins the pass carrying it with an empty value — so the label stays
 *    and the em-dash, which is purely ours, goes.
 *  - DEVICE view, `"awaited"`: the bar. The renderer fills this row from data no
 *    client can see; drawing nothing under its label states the opposite.
 *  - DEVICE view, `"absent"`: {@link HIDE_ROW}. There is no value, both
 *    renderers delete the row, and so does this view.
 *
 * A plain function and NOT a hook: `FieldRow` asks it once per field inside a
 * `.map`, which is exactly where a hook may not go. The ambient answers it
 * needs are read once at the top of the component and passed in.
 */
export function emptyValueMark(opts: {
  deviceOnly: boolean;
  certainty: RowCertainty | undefined;
  labelColor: string;
}): EmptyValueMark {
  const { deviceOnly, certainty, labelColor } = opts;
  if (!deviceOnly) return <span style={{ color: labelColor }}>—</span>;
  if (certainty === "absent") return HIDE_ROW;
  if (certainty === "awaited") return <UnknownValueBar color={labelColor} />;
  return null;
}

const TOKEN_RE = /(\{\{[^}]+\}\})/g;

/**
 * What a token draws, or `null` when it draws nothing at all.
 *
 * Three cases, and the third is the reported bug. A token we have a sample for
 * draws the sample. A token we have none for draws its own NAME — an honest
 * placeholder for a custom additional field, where the name is genuinely the
 * best we can say. But an EVENT token missing from a REAL event's values is
 * neither: `buildEventVariables` skips empty sources, so its absence means the
 * event has no such value and the renderer will substitute nothing. Drawing
 * "eventLocation" there put a plausible English word on the pass face for a row
 * the guest never receives.
 *
 * That third case is why this still runs in the design lane even though that
 * lane draws the TOKEN rather than the sample: `null` here means the pass shows
 * nothing, so the chip must not appear either. A lane that skipped this and
 * chipped every token it found would put `{{eventLocation}}` on the face of a
 * card whose delivered row is deleted — the same lie in different type.
 */
/**
 * The token name the RENDERER will look up, or null for one it cannot.
 *
 * `TemplateVarSubstitutor.substitute` captures with `\{\{([^{}]+)\}\}` and looks
 * the group up EXACTLY — `String key = m.group(1)`, no trim — and blanks a miss.
 * No payload key carries surrounding spaces, so `{{ eventLocation }}` ships
 * nothing whatever the event holds.
 *
 * Both sample lanes used to trim before looking up, which drew the venue for a
 * row the pass leaves blank. That was survivable while the drop rule trimmed
 * too — the row was deleted before anything drew it. Once the drop rule was
 * corrected and this was not, a MIXED value opened a new gap:
 * `"{{ eventLocation }} (Saal)"` blanks only the token, so " (Saal)" is
 * non-empty and the row survives — and this lane then drew
 * "Villa Elisabeth (Saal)" over a pass carrying " (Saal)".
 */
function rendererName(raw: string): string | null {
  return raw === raw.trim() ? raw : null;
}

function sampleForToken(token: string, samples: WalletSamples): string | null {
  // The DOTTED form is never an event variable. `{{additionalFields.eventLocation}}`
  // is a custom field that happens to share a name, and popping the last segment
  // first made the chip treat it as one: it drew nothing, while the drop rule —
  // which reads the full token — correctly kept the row. A surviving row with an
  // invisible value. The rule and the chip have to agree on what a token IS.
  const bare = !token.includes(".");
  const key = token.split(".").pop() || token;

  // Both halves of that, not just the second. `samples.values` is the guest
  // sample map with the REAL event's values layered on top, so looking a popped
  // last segment up in it lets a dotted token borrow an event value outright:
  // `{{additionalFields.eventLocation}}` for an event that HAS a venue drew
  // "Hotel de Rome" into a custom field the guest may never have filled. The
  // first version only fixed the venue-less case and its test only covered that
  // case, which is how a half fix reads as a whole one.
  //
  // The bare token name is the honest answer here: it reads as a placeholder,
  // and the drop rule — which sees the full token and cannot decide it either —
  // keeps the row.
  if (!bare && EVENT_VARIABLE_KEYS.has(key)) return key;

  const known = samples.values[key];
  if (known !== undefined) return known;
  if (bare && samples.eventLoaded && EVENT_VARIABLE_KEYS.has(key)) return null;
  return key;
}

function renderTemplate(
  text: string,
  samples: WalletSamples,
  deviceOnly: boolean,
): React.ReactNode[] {
  return text.split(TOKEN_RE).map((part, i) => {
    const m = part.match(/^\{\{([^}]+)\}\}$/);
    if (!m) return <span key={i}>{part}</span>;
    const resolvable = rendererName(m[1]);
    // A padded token draws NOTHING, because the pass carries nothing.
    if (resolvable === null) return null;
    const name = sampleForToken(resolvable, samples);
    if (name === null) return null;
    // The chip is the single largest thing standing between this card and the
    // pass: it tints a box behind the text AND adds 8px of horizontal padding
    // per token, which is what pushed a two-token value over its box.
    if (deviceOnly) return <span key={i}>{name}</span>;
    // The DESIGN lane draws the token, not its sample.
    //
    // This reverses what stood here, and the reversal was a product decision
    // rather than a measurement, so the reasoning it overrules is worth keeping
    // legible. The sample lane existed because LENGTH is the only quantity an
    // operator can get wrong on a pass — Apple owns the font and the sizes —
    // and a card drawn from tokens is not the width of the delivered one.
    //
    // The direction is not uniform, which an earlier version of this paragraph
    // got wrong by reasoning from the token NAME (`company`, 7 characters)
    // rather than from what is drawn. Measured in a browser, in the ~143px cell
    // that two fields share on a 320px card:
    //
    //   {{…company}}                 12 chars, fits    | ships 24, clipped
    //   {{firstName}} {{lastName}}   26 chars, 222px   | ships 23, 212px
    //
    // So the design lane under-draws some values and over-draws others. It is
    // not a safety margin in either direction and must not be read as one.
    //
    // What makes that acceptable: the two things that actually WARN about
    // length do not read this function at all. The header-collision model
    // measures `resolve(...)` — the shipping string, capped the way the
    // renderer caps it — and the over-limit markers count fields, not glyphs.
    // So the warning still fires on a value that will collide even though the
    // chip beside it is short. And the 1:1 lane above still draws the full
    // sample, which is where a length judgement belongs.
    //
    // What it buys: the design lane stopped reading as though someone had typed
    // "Alexandra Weber-Hofmann" into the template. It had not — the template
    // holds `{{firstName}} {{lastName}}` — but nothing on the card said so, and
    // the caption's "Beispielwerte" line was doing all that work alone.
    //
    // `resolvable` rather than `m[1]`: they are equal here (a padded token
    // returned above), and using the checked value keeps the chip and the drop
    // rule reading the same string.
    //
    // A DOTTED token is drawn with its namespace elided: `{{…company}}`, not
    // `{{additionalFields.company}}`. Measured in a browser at the real cell
    // width — two fields share a 320px card, so a cell is ~143px — the full
    // token needs 233px and `overflow-hidden` cuts it to `{{additionalFields.`,
    // which is the half that says NOTHING about which field it is. Two
    // different custom fields drew identically. The elision is at the front
    // because the identifying part is at the back.
    //
    // Not just the last segment (`{{company}}`): a custom field named `company`
    // would then be indistinguishable from the built-in `{{company}}`, which is
    // the same confusion one level down. The ellipsis says "there is more here"
    // and `title` carries the whole thing for anyone who needs it.
    const full = `{{${resolvable}}}`;
    // `lastSegment` can be EMPTY — `{{additionalFields.}}` is hand-typeable in
    // the free-text variable input, and eliding it produced `{{…}}`, which
    // names nothing at all and is strictly worse than the token it replaced.
    // Elide only when something is left to keep.
    const lastSegment = resolvable.slice(resolvable.lastIndexOf(".") + 1);
    const shown =
      resolvable.includes(".") && lastSegment.length > 0
        ? `{{…${lastSegment}}}`
        : full;
    return (
      <span
        key={i}
        // The FULL token, always, even when the visible text is elided. Three
        // readers need it and none can read an ellipsis: the e2e spec guarding
        // the original customer report (a chip must never read a bare field
        // name where a value belongs) — it used to find the chip by
        // `span.inline-flex`, which silently matched NOTHING once the chip
        // became `inline`, turning its absence assertion vacuously green;
        // anything else asserting on the card; and a reader wondering which
        // `firstName` this is, because the elision is not injective —
        // `mainGuest.firstName` and `additionalFields.firstName` shorten alike.
        data-merge-field={full}
        // Mouse-only, and that is a real limit rather than a solved problem:
        // `title` is not keyboard-reachable, does not exist on touch, and is
        // not part of the accessible name of a generic `span`. The house
        // convention is `HoverInfo` + `cursor-help`, which cannot go inside a
        // pass face without putting builder chrome on the card. So for keyboard
        // and touch operators the namespace is genuinely absent from the
        // picture; the attribute above is what keeps it in the DOM.
        title={shown === full ? undefined : full}
        // `normal-case` because field LABELS are `uppercase`, and a merge field
        // shouted as `{{FIRSTNAME}}` reads like a different token than the one
        // in the editor — `TemplateVarSubstitutor` looks names up exactly, so
        // an operator who copies what they see ships a blank row.
        //
        // `inline`, not `inline-flex`, and that is the whole fix for labels.
        // The label line is `truncate`, and `text-overflow` cannot split an
        // ATOMIC box: against an `inline-flex` chip the browser drops the chip
        // whole and parks the "…" in the text before it, so a label reading
        // "Ticket for {{firstName}}" drew "TICKET FOR …" and the merge field
        // vanished from the card entirely. An `inline` span is ordinary text
        // as far as truncation is concerned, so it shortens to
        // "TICKET FOR {{firstName…" instead. Measured in a browser both ways;
        // `max-w-full overflow-hidden` on the chip was the first attempt and
        // changed nothing, because the drop happens in the PARENT's
        // text-overflow step, not inside the chip.
        //
        // The dropped `items-center` / `align-baseline` cost nothing here: the
        // chip's own line box already sits on the text baseline, and the value
        // rows measure identically before and after.
        className="inline rounded-[4px] px-1 font-medium normal-case"
        style={{
          backgroundColor: "color-mix(in srgb, currentColor 14%, transparent)",
        }}
      >
        {shown}
      </span>
    );
  });
}

/**
 * The three ways a preview bit needs to look at pass text, all bound to the
 * same sample set: draw it with `{{token}}` chips, resolve it to the string
 * that will ship, or ask what a field will actually read (which is not the same
 * question once the device formats the date).
 *
 * A hook rather than free functions because the sample set is per-pass now —
 * it carries the real event's name, date and time. Free functions would have
 * meant threading it through six preview components by hand, which is the shape
 * drift takes.
 */
export function useTemplateRenderer() {
  const samples = useWalletSamples();
  const deviceOnly = useDeviceOnly();
  return React.useMemo(
    () => ({
      /** Draw with merge-field chips — plain text in the device view. */
      render: (text: string) => renderTemplate(text, samples, deviceOnly),
      /** The plain text as it will ship. */
      resolve: (text: string) => resolveSampleText(text, samples),
      /** What the field will read on the device, date formatting included. */
      fieldText: (field: WalletField, lang: string, deviceFormatsDates = true) =>
        fieldSampleText(field, lang, samples, deviceFormatsDates),
      /**
       * A per-language map, read as tolerantly as the renderer reads it.
       * The drop rule already resolves exact → base → primary → first; drawing
       * by exact index instead left a field that survived the rule rendering an
       * em-dash, which is the reported symptom one layer down.
       */
      text: (map: Record<string, string> | undefined, lang: string) =>
        resolveLangValue(map, lang, samples.primaryLang, ""),
    }),
    [samples, deviceOnly],
  );
}

/**
 * How many fields of each group the DEVICE draws, for this config.
 *
 * Wraps the schema so no preview component re-derives the limits — the combined
 * secondary+auxiliary cap on coupon/storeCard/generic with a square barcode is
 * not expressible as a per-group number, and duplicating that rule five times
 * is how it would drift.
 */
export function useVisibleCounts(config: WalletConfig) {
  const schema = getSchema(config);
  const combined = getVisibleFieldCounts(config, {
    secondary: config.secondaryFields?.length ?? 0,
    auxiliary: config.auxiliaryFields?.length ?? 0,
  });
  return {
    header: getFieldGroupMax(schema, "header"),
    primary: getFieldGroupMax(schema, "primary"),
    secondary: combined.secondary,
    auxiliary: combined.auxiliary,
  };
}

type FieldRowProps = {
  /**
   * How many of these fields the DEVICE will actually draw. Anything past it
   * is dimmed and tagged rather than hidden — see the note at the map below.
   */
  visibleCount?: number;
  /**
   * Whether a field's stored `textAlignment` is drawn. Off only where the
   * CONTAINER already pins alignment — see the header overlay's call site.
   */
  honourAlignment?: boolean;
  /**
   * Shrink an over-long value to fit its cell — the device's own behaviour.
   *
   * Off for Google, for the same reason `deviceFormatsDates` and
   * `honourAlignment` are off there: a Google text module WRAPS its body, it
   * does not shrink the type. Shrinking one module would draw a row smaller
   * than its neighbours on a card where every module is one size — a picture
   * that platform never produces.
   *
   * The Google lane now goes further and sets `wrapValue`, which bypasses
   * `FitText` altogether, so `fit={false}` changes nothing there any more. It
   * stays spelled out at that call site because `fitTextCallSites.test.tsx`
   * greps for it — it is the record of the decision, not the mechanism.
   */
  fit?: boolean;
  /**
   * How a shared row distributes the space its fields do not use.
   *
   * `between` is the card body: first field at the row's start, last at its end,
   * slack in the middle — measured off a device (see the cell comment below).
   * `end` is the header, whose container is already pinned to the card's right
   * edge; there the fields pack against that edge instead of spreading across a
   * 70%-wide box.
   *
   * Deliberately its own prop rather than reading `honourAlignment`. Both are
   * false in the same one place today, and a single flag carrying two meanings
   * is how the next caller gets the wrong one.
   */
  justify?: "between" | "end";
  fields: WalletField[] | undefined;
  currentLanguage: string;
  colors: PreviewColors;
  layout?: "row" | "column";
  valueClassName?: string;
  /** Label size, for the header, whose labels are not secondary-field labels. */
  labelClassName?: string;
  /** Drawn value size in px. Defaults to the measured field size; the header
   *  overrides it, because the device sets that row a point larger. */
  valuePx?: number;
  /**
   * Whether the PLATFORM lets the device format a date field. True for
   * PassKit. False for Google, whose text modules take a string — the
   * consumer reads straight past dateStyle there, so formatting in the
   * preview would show a card the platform never draws.
   */
  deviceFormatsDates?: boolean;
  /**
   * What the delivered pass will do with a row this client could not decide —
   * see {@link useRowCertainty}, which is what a caller fills it from.
   *
   * A callback rather than a key set, because only the caller knows which GROUP
   * it is drawing and keys are minted per group (`field_1` exists in several at
   * once in an ordinary editor-built pass). `GooglePassPreview` flattens all
   * five groups into one list and therefore builds its own callback over
   * `{field, group}` pairs; the index is passed so such a caller can use it.
   */
  certaintyOf?: (field: WalletField, index: number) => RowCertainty | undefined;
  /**
   * Let a long value run onto further lines instead of clipping it to one.
   *
   * Off for every Apple lane, for the reason argued at the value slot below:
   * PassKit never wraps a field value. On for the Google card, where the
   * opposite is true — a text module WRAPS its body and has no type-fitting
   * step — and where clipping became actively misleading once the right-hand
   * column was right-aligned: a right-anchored one-line box loses the START of
   * the value, so a guest name read "kita Rafael Reichert".
   */
  wrapValue?: boolean;
};

/**
 * A stored `textAlignment` as CSS, or null for one the renderer ignores.
 *
 * Mirrors `PassJsonBuilder.pkTextAlignment` exactly, including its tolerance:
 * trimmed and lower-cased, four accepted words, everything else null. `natural`
 * is CSS `start`, which is the same rule PassKit applies — the reading
 * direction of the content's own language.
 */
export function alignOf(raw: unknown): "left" | "center" | "right" | "start" | null {
  if (typeof raw !== "string") return null;
  switch (raw.trim().toLowerCase()) {
    case "left":
      return "left";
    case "center":
      return "center";
    case "right":
      return "right";
    case "natural":
      return "start";
    default:
      return null;
  }
}

/**
 * Whether the DEVICE view draws this row at all — `FieldRow`'s own filter, as a
 * predicate a caller can ask BEFORE it lays the fields out.
 *
 * It exists for the Google card. That lane hands the first four DELIVERED front
 * modules to the card's row slots (`buildGenericObject` in main-consumer decides
 * a slot after its drop rule), so it has to know the survivors before it slices
 * — slicing the configured list and letting `FieldRow` drop one afterwards left
 * a three-cell front and sent the field that moves up on the real card to the
 * details sheet instead. One predicate, used by `FieldRow` too, so the two
 * cannot disagree about which rows exist.
 *
 * The builder view keeps every row; its marks are the point of that view.
 * `visibleCount` is NOT part of this: that is a per-row device cap and stays
 * with `FieldRow`.
 */
export function useDeviceKeepsRow(currentLanguage: string) {
  const deviceOnly = useDeviceOnly();
  const { resolve, text } = useTemplateRenderer();
  return React.useCallback(
    // `unknown`, not `EmptyValueMark`: the only question asked of it is whether
    // it IS the sentinel, and a `unique symbol` widens to `symbol` the moment it
    // rides through an object literal at the call site.
    (field: WalletField, mark: unknown): boolean => {
      if (!deviceOnly) return true;
      const raw = text(field.value, currentLanguage);
      if (
        rendererDropsRow(
          resolve(raw ?? ""),
          raw,
          resolve(text(field.label, currentLanguage) ?? ""),
        )
      )
        return false;
      // A row the pass will not carry goes WITH ITS LABEL. Filtered rather
      // than answered in the value slot, so the cell disappears instead of
      // leaving a labelled empty column in the row — which is the very picture
      // this change is about.
      return !!raw || mark !== HIDE_ROW;
    },
    [deviceOnly, resolve, text, currentLanguage],
  );
}

export function FieldRow({
  fields,
  currentLanguage,
  colors,
  layout = "row",
  valueClassName,
  labelClassName,
  valuePx,
  visibleCount,
  deviceFormatsDates = true,
  honourAlignment = true,
  fit = true,
  justify = "between",
  certaintyOf,
  wrapValue = false,
}: FieldRowProps) {
  const deviceOnly = useDeviceOnly();
  const { render, resolve, fieldText, text } = useTemplateRenderer();
  const deviceKeepsRow = useDeviceKeepsRow(currentLanguage);
  if (!fields || fields.length === 0) return null;
  const labelSize = labelClassName ?? "text-[10px]";
  // A caller that brings its own class owns the whole size decision (the
  // primary lanes do); everything else gets the measured device size.
  const valueSize = valueClassName ?? "font-medium";
  // `lineHeight` alongside the size, not left to the browser's default 1.5.
  // The device sets these rows tight — its header label and value span 28.9pt
  // of ink together where a default line box would claim over 40 — and the
  // header's field stack is absolutely positioned, so any leading it gains
  // overflows the row it is pinned into rather than growing it.
  const valueStyle = valueClassName
    ? undefined
    : { fontSize: `${valuePx ?? FIELD_VALUE_PX}px`, lineHeight: 1.15 };
  // PassKit does NOT divide a row into equal columns. Every cell is
  // content-sized; the row's spare width goes between them.
  //
  // MEASURED off a customer's device against the same pass in the builder, on a
  // two-field secondary row (guest name + time):
  //
  //   device   left field 61.4% of the card | gap 6.6% | right field 17%, at the edge
  //   preview  left field 44.2%, cut EXACTLY at the card's midpoint (7px out)
  //
  // The name the phone drew in full — "Maurice-Nikita Rafael Reichert" — was cut
  // to "Maurice-Nikita Raf" here, and the cut lands on the half-column boundary
  // `flex-1` creates. Not a font-size difference: the glyph height is 38px in
  // BOTH device fields, so nothing was shrunk; the left field was simply given
  // more than half the row.
  //
  // `flex-initial` (`flex: 0 1 auto`), not `flex-none`: content-sized while it
  // fits, and still shrinking — with `min-w-0` — when the row's fields together
  // outgrow it. That overflow case is real and measured (3 header fields want
  // 229.7px of 204.9px), and the clipping argument below is written for it.
  // `flex-none` would have pushed the surplus out of the card instead.
  //
  // ...except for the LAST drawn cell, which does not shrink. On the device the
  // right-hand field is whole and the left one absorbs the shortfall: the phone
  // drew "09:00 Uhr" complete beside a guest name it had cut. Letting both
  // shrink proportionally — the first thing this change did — clipped the time
  // to "09:00 Uh", which is a value no pass has ever shown. The row is
  // right-anchored; the surplus comes off the left.
  //
  // The cap on it is not decoration, and `max-w-full` was not enough.
  //
  // `flex-none` is shrink 0, so a last cell wider than the row keeps its content
  // width and pushes itself past the card — measured at 180.1px beyond the right
  // edge, inside a `PassCard` that is `overflow-hidden`, so the surplus is
  // invisible and drags the row with it. `max-w-full` bounded that. It did not
  // bound the case that matters more: with a long value on the RIGHT — "Ort:
  // Kongresszentrum Hamburg Messehallen" beside a guest name — the last cell
  // legitimately claims the whole row and the FIRST cell resolves to zero
  // width. Both its label (`truncate`) and its value (`overflow-hidden`) then
  // render nothing at all: the field disappears from the card with no marker
  // and no caption. Reproduced in a browser; the measuring script's own
  // selector reported the cell as not visible.
  //
  // That is the failure this file argues against three times over — "removing
  // it would make a customer's existing field vanish from their builder with no
  // explanation" — arrived at from the other side.
  //
  // 75% is a floor against disappearance, not a law. The one device sample puts
  // the right-hand field at 21.1% of the card against the left field's 63.5%,
  // so a right field wanting more than three quarters of the row is already
  // outside anything measured. What the device does there — shrink the type, on
  // the evidence of its own docs — this preview does not model. Leaving a
  // quarter of the row for everything else is the least wrong answer available
  // until somebody measures a pass with a value that long.
  //
  // WHICH SIDE WINS when the two together do not fit is a SEPARATE choice from
  // the 75% floor, it is made here, and it is NOT measured. `flex-none` on the
  // trailing cell means the trailing field keeps its content width and the
  // LEADING one absorbs the whole shortfall. The device sample cannot settle
  // it: there both fields fitted, so "leading wins" and "trailing wins" predict
  // the identical picture. It was chosen because the trailing field is
  // typically the short one (a time, a seat, a gate) and clipping "09:00 Uhr"
  // to "09:00 Uh" is the more visible damage — a reason, not evidence.
  //
  // A browser sweep over all five Apple types plus the Google card, with a long
  // leading value ("Kongresszentrum Hamburg Messehallen") and a realistic
  // trailing one, on a 320px card: nothing leaves the card, the trailing cell
  // sits flush on the inset in every case, and the leading cell gets 54–70% —
  // past the midpoint the old `flex-1` cut it at, which is the whole point. In
  // the inverted case (short leading, long trailing) the cap binds exactly at
  // 75% and the leading field keeps its content width rather than collapsing.
  // What no measurement covers is whether the DEVICE would have shrunk the type
  // instead of clipping either one.
  //
  // A lone field stays `flex-none … max-w-full` as before: content-sized at the
  // row's start, so `textAlignment` has nothing to move it within — the device
  // draws a lone RIGHT field on the LEFT.
  //
  // This is the same reasoning the strip overlay states for the opposite
  // conclusion: there `flex-1` is right, because a primary field always owns
  // the whole band. Row width follows the row's own field count, nowhere else.
  //
  // Counted over the fields that are actually DRAWN, not the ones configured.
  // `fields.length` was wrong twice over in the device view: a sibling past the
  // device's cap is dropped below, and a sibling the renderer deletes outright
  // is dropped too — either way the survivor is alone on its row and must be
  // content-sized, exactly as a lone field always was. Counting the config
  // instead left the reported bug live in the one mode whose entire claim is
  // that it draws what the device draws.
  // The original index rides along rather than being looked up again: two
  // fields can legitimately be the same object, and `indexOf` would then hand
  // both of them the first one's position.
  // The mark is computed ONCE per field and carried to the value slot. It used
  // to be computed twice — here and below — from two hand-written argument
  // objects that had to stay in step; that is a divergence waiting to happen,
  // and the two answers decide different things (whether the row exists, and
  // what stands in it).
  const drawn = fields
    .map((field, i) => ({
      field,
      i,
      mark: emptyValueMark({
        deviceOnly,
        certainty: certaintyOf?.(field, i),
        labelColor: colors.lbl,
      }),
    }))
    .filter(({ field, i, mark }) => {
      if (!deviceOnly) return true;
      if (visibleCount !== undefined && i >= visibleCount) return false;
      return deviceKeepsRow(field, mark);
    });
  if (drawn.length === 0) return null;
  const sharesRow = layout === "row" && drawn.length > 1;
  return (
    <div
      // `flex-nowrap`. PassKit does not wrap a field row — it shrinks the type
      // until the row fits. Wrapping was the preview's own invention and it
      // cost more than a wrong picture: the header fields are pinned
      // ABSOLUTELY inside a row whose height they do not contribute to, so a
      // second line put ~40px of them straight through the collision warning
      // and over the strip. Three header fields is the schema maximum, not an
      // exotic config.
      className={`flex ${
        layout === "row"
          ? `flex-row flex-nowrap ${justify === "end" ? "justify-end" : "justify-between"}`
          : "flex-col gap-2"
      }`}
      style={layout === "row" ? { columnGap: `${FIELD_GAP_PX}px` } : undefined}
    >
      {drawn.map(({ field, i, mark }, drawnIndex) => {
        const isLastDrawn = drawnIndex === drawn.length - 1;
        const value = text(field.value, currentLanguage);
        const formatted = fieldText(field, currentLanguage, deviceFormatsDates);
        const appleFormats = formatted !== resolve(value ?? "");
        // Beyond what the device draws. Kept visible but marked: removing it
        // would make a customer's existing field vanish from their builder
        // with no explanation, and the point is to inform, not to edit. The
        // device view has already filtered these out of `drawn` above.
        const beyond = visibleCount !== undefined && i >= visibleCount;
        // `textAlignment` is stored on the field, mapped by
        // `PassJsonBuilder.pkTextAlignment` to PKTextAlignmentLeft/Center/Right/
        // Natural, and written into pass.json — so the DEVICE has always honoured
        // it while this preview drew every row left. A template that carries one
        // (an import, or a schema that moved on) looked one way in the builder
        // and another in the guest's phone, with nothing saying so. Unknown and
        // absent values fall through to the renderer's own answer: no key, so
        // the device's default.
        const align = honourAlignment ? alignOf(field.textAlignment) : null;
        return (
          <div
            // Position AND key. The key alone is not unique on the Google
            // details sheet, which draws several sections in one list:
            // `nextFieldKey` numbers within a section, so an auxiliary
            // `field_1` beside a back `field_1` is the ordinary editor-built
            // shape. React warned, and after a delete it kept the removed row
            // on screen until the next remount. `i` is the field's index in
            // `fields`, so it is stable across the device-view filter.
            key={`${i}:${field.key ?? ""}`}
            style={align ? { textAlign: align } : undefined}
            // `min-w-0`, not a 72px floor. With `flex-nowrap` a floor cannot
            // be honoured and overflowed instead: 3 header fields need 229.7px
            // of a 204.9px box, 4 secondary fields 308.5px of 292.7px, and a
            // boarding pass's 5 auxiliary fields 387.3px — all of it running
            // out past `PassCard`'s `overflow-hidden`, taking the rightmost
            // field off the card entirely. Letting the cells shrink and clip
            // their own text keeps every field on the card and is the closest
            // this can get to the device, which shrinks the type instead.
            className={`${
              sharesRow
                ? isLastDrawn
                  ? "flex-none min-w-0 max-w-[75%]"
                  : "flex-initial min-w-0"
                : "flex-none min-w-0 max-w-full"
            } ${beyond ? "opacity-40" : ""}`}
            // A stable hook for "one drawn field". The alternative in use was
            // `.flex-none, .flex-1` — a class list, which broke the moment the
            // shared row stopped being two equal columns. Counting cells is a
            // legitimate thing for a test to want; making it depend on the
            // layout it is not testing is not.
            data-field-cell={field.key ?? ""}
            {...(beyond
              ? { "data-beyond-limit": "true", "data-field-key": field.key }
              : {})}
          >
            {/* No fallback to `field.key`: the renderer writes an EMPTY label
                rather than the internal key, because "seatNumber" on a
                customer's German ticket is worse than nothing. A preview that
                filled the gap with the key showed a caption the device never
                draws — and hid that the row ships without one. */}
            <div
              className={`${labelSize} uppercase tracking-wider truncate`}
              style={{ color: colors.lbl }}
            >
              {text(field.label, currentLanguage)
                ? render(text(field.label, currentLanguage))
                : "\u00a0"}
            </div>
            {/* No `truncate`. The renderer ships the value verbatim
                (`PassJsonBuilder.putSection` writes `value` untouched, and the
                only ellipsize in the whole builder is the logoText FALLBACK),
                and the device shrinks a long value rather than cutting it. A
                CSS ellipsis here invented a shortening nobody would see —
                worse, it did so at a box ~16% narrower than Apple's, so it
                fired on values that FIT on the phone. The chip made it look
                stranger still: `inline-flex` is an atomic box that
                `text-overflow` cannot split, so the browser dropped a whole
                chip and parked the "…" outside the one before it.

                This is the same call the brand line already made one block up
                ("a courtesy the device does not extend"): overflow honestly,
                rather than lie tidily. The label keeps its `truncate` — no
                device measurement says what PassKit does to an over-long
                LABEL, and inventing symmetry here would be the same guess in
                the other direction.

                `whitespace-nowrap overflow-hidden` instead. PassKit never
                WRAPS a field value either — it shrinks the type until the
                value fits its column — so letting the text run onto a second
                line traded one wrong picture for another, and a worse one:
                wrapping pushes every row below it down, so the card's whole
                geometry goes wrong rather than just one value's. One line,
                clipped at the cell it belongs to, keeps the rows where the
                device puts them and leaves a visibly cut edge where an
                operator needs to see one. Clipped at the CELL, not the card,
                so an over-long value cannot run across the value beside it.

                No `text-overflow` with it, deliberately: the ellipsis is both
                the lie and the thing that made a merge-field chip vanish
                whole, since it cannot split an atomic inline box. */}
            {wrapValue ? (
              // The Google lane. A plain block, not `FitText`: that component
              // is a one-line box by construction (a `flex-none` span that
              // cannot be squeezed), and this lane has already opted out of
              // the only thing it does. `break-words` so one unbroken string —
              // an e-mail address, a URL — cannot push the cell past the card.
              <div
                className={`${valueSize} break-words`}
                style={{ color: colors.fg, ...valueStyle }}
              >
                {!value ? (mark as React.ReactNode) : appleFormats ? formatted : render(value)}
              </div>
            ) : (
              <FitText
                // No `align` prop: `FitText` reads the cell's own `text-align`,
                // which is where `alignOf(field.textAlignment)` already put this
                // field's answer — and, on the header row, where the CONTAINER
                // put it. Passing one here overrode the container on the one row
                // that has no field alignment of its own, and the header values
                // slid 77px off their measured right pin.
                fit={fit}
                className={`${valueSize} whitespace-nowrap overflow-hidden`}
                style={{ color: colors.fg, ...valueStyle }}
              >
                {!value ? (
                  // The renderer DELETES a row whose value resolves empty
                  // (`PassJsonBuilder.putSection` continues past it), so
                  // the device shows neither the value nor its label. The
                  // em-dash is the builder saying "nothing here yet"; in
                  // the device view it would be the one mark on the card
                  // that is purely ours — EXCEPT where the row is one the
                  // client could not decide, which is a different fact and
                  // gets a different mark. `emptyValueMark` holds the whole
                  // rule; `HIDE_ROW` was already filtered out above, and a
                  // symbol is not renderable, so a slip here is a type error.
                  (mark as React.ReactNode)
                ) : appleFormats ? (
                  // Handed to the device: what is drawn here is what Intl makes
                  // of the event's instant, which is what PassKit will do too —
                  // except for the LANGUAGE, which is the guest's on the device
                  // and the builder's here (the field editor says so).
                  formatted
                ) : (
                  render(value)
                )}
              </FitText>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Shrink a one-line value until it fits its cell — what the DEVICE does.
 *
 * Four comments in this file already stated it ("PassKit never wraps a field
 * value either — it shrinks the type until the value fits its column") and the
 * preview did the other thing: it clipped, deliberately, because clipping keeps
 * the row heights honest while wrapping moves every row below it. That was the
 * right call against WRAPPING. It was never the right call against SHRINKING,
 * which is what the device actually does and which moves nothing at all.
 *
 * `transform: scale`, not a smaller `font-size`, and the reason is both
 * correctness and safety:
 *  - GEOMETRY. A transform is applied after layout, so the cell keeps exactly
 *    the height and width it has today. Every row below stays where the device
 *    puts it — the property the clipping decision was protecting.
 *  - NO FEEDBACK LOOP. `scrollWidth` is a layout value and a transform does not
 *    change it, so measuring after scaling yields the same number and the
 *    observer settles in one pass. Shrinking `font-size` instead re-lays-out
 *    the text, which changes the measurement that decided the shrink — the
 *    oscillation `FitBox` in `AttachmentPreview` had to solve explicitly.
 *
 * There is a FLOOR, and it is a PIXEL size, not a ratio. Scaling without one
 * turns a long value into a grey smear; past a point an operator is better
 * served by a visibly cut edge than by unreadable text. Below the floor the
 * value keeps the floor's size and the cell's existing `overflow-hidden` clips
 * it, exactly as before this existed.
 *
 * A ratio was the first attempt and it was measurably wrong. The lanes this
 * wraps span a factor of three in type size — a field value renders at 17.1px
 * and the strip primary at 55.5px on a 320px card (measured). One ratio cannot
 * serve both: 0.62 took the field value to 10.6px, right at the readability
 * limit and correct, while it stopped the strip primary at 34.4px, which is
 * nowhere near any limit and left a 39-character event name cut in half for no
 * reason. Readability is an absolute size, so the floor is one:
 * {@link MIN_FIT_TEXT_PX}. The same 10.5px bound now lets the strip go to 0.19
 * — where that event name fits whole — and still refuses to take a 17px field
 * value below 0.61.
 *
 * NOT applied to the brand line. Its overlap with the header fields is MEASURED
 * device behaviour (three observations, recorded at `PassHeader`), and the
 * collision warning is built on it. Shrinking there would hide a real defect
 * behind a tidy picture.
 *
 * NOTHING IN THE SUITE CAN SEE THIS. vitest runs jsdom with `css: false`, so
 * `scrollWidth` and `clientWidth` are both 0, the guard below returns early and
 * the scale stays 1 — which is also why adding this changes no existing test.
 * The only evidence that it works is a browser measurement.
 */
/**
 * Smallest rendered text size, in CSS pixels.
 *
 * 10.5, because the smallest lane this wraps already renders at 17.1px and the
 * ratio floor that shipped first took it to 10.6px — a value read and accepted
 * on screen. Anything under this is a shape, not a word, and a cut edge is the
 * more honest picture.
 */
const MIN_FIT_TEXT_PX = 10.5;

export function FitText({
  align,
  fit = true,
  minPx = MIN_FIT_TEXT_PX,
  originY = "bottom",
  className,
  style,
  children,
}: {
  /**
   * Which edge stays put while the text shrinks.
   *
   * OMIT IT to inherit the box's own computed `text-align`, and that is the
   * right answer for most lanes. Defaulting to `"left"` was a regression with
   * a measured cost: the header field stack is right-pinned by its CONTAINER
   * (`absolute right-0 … text-right`) and passes `honourAlignment={false}`, so
   * its cells have no field alignment of their own — they were handed the
   * default and a `justify-content: flex-start`, and `text-align` cannot
   * position a flex item. A header field whose LABEL is wider than its value
   * (a long label, a short date) went from sitting 13.6px off the card edge —
   * exactly `PASS_INSET_PX` — to 90.8px, with the truncated label still pinned
   * right above it. The header is the one row in this file rebuilt against a
   * device screenshot; it must not drift because a default was convenient.
   */
  align?: "left" | "right" | "center";
  /**
   * Shrink at all. `false` keeps the pre-existing clip-only behaviour while
   * still anchoring the box. No lane uses that today: it was the Google card's
   * setting until that card stopped mounting `FitText` at all (`wrapValue` —
   * a Google text module wraps, which a one-line box cannot draw).
   */
  fit?: boolean;
  /** Smallest rendered size in px. Below it: clip, as before. */
  minPx?: number;
  /**
   * Which horizontal line stays put while the text shrinks. `"bottom"` (the
   * default) keeps a shrunk value on the baseline its row neighbours sit on —
   * see the transform below. `"center"` is for a value centred in a band of its
   * own, the event ticket's strip primary: the device keeps a shrunk title on
   * the band's middle, and the bottom anchor dropped it toward the band's lower
   * edge — measured 14.4px at s=0.48, 20.9px at s=0.246 — off the artwork's
   * middle, so a title the device lays across a centred motif sat below it.
   */
  originY?: "bottom" | "center";
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}) {
  const box = React.useRef<HTMLDivElement>(null);
  const text = React.useRef<HTMLSpanElement>(null);
  const [scale, setScale] = React.useState(1);
  /** The box's own `text-align`, resolved once it is in the document. */
  const [inherited, setInherited] = React.useState<"left" | "right" | "center">(
    "left",
  );

  React.useLayoutEffect(() => {
    const outer = box.current;
    const inner = text.current;
    if (!outer || !inner) return;
    const measure = () => {
      // `scrollWidth` on the inline-block span is the text's NATURAL width: it
      // is not constrained by the parent, because the parent is `nowrap`.
      const need = inner.scrollWidth;
      const have = outer.clientWidth;
      // jsdom, a display:none ancestor, a cell mid-layout — all report 0, and
      // 0/0 would set the scale from nothing. Leave it alone.
      if (!need || !have) return;
      // The floor as a RATIO for this lane, derived from its own rendered
      // size. `getComputedStyle` rather than a prop: the lanes set their type
      // through Tailwind classes and, on the strip, a computed `fontSize` —
      // asking the box is the only way to know what it actually renders at.
      const cs = getComputedStyle(outer);
      // Read in the SAME pass as the size: both come from the cascade, and a
      // lane can change either without re-mounting (the platform toggle, a
      // preset with its own `valuePx`, a stored `textAlignment`).
      const ta = cs.textAlign;
      setInherited(
        ta === "right" || ta === "end"
          ? "right"
          : ta === "center"
            ? "center"
            : "left",
      );
      const fontPx = parseFloat(cs.fontSize);
      // A box with no readable font size (0, NaN, jsdom) gets no floor rather
      // than a nonsensical one; the `!need || !have` guard above has already
      // returned in every such case this can reach, so this is belt and braces.
      const floor =
        Number.isFinite(fontPx) && fontPx > 0 ? Math.min(1, minPx / fontPx) : 1;
      const next = !fit || need <= have ? 1 : Math.max(floor, have / need);
      // Round before comparing: sub-pixel churn on a resize would otherwise
      // schedule a render per observed frame.
      setScale((prev) =>
        Math.abs(prev - next) < 0.005 ? prev : Math.round(next * 1000) / 1000,
      );
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(outer);
    ro.observe(inner);
    return () => ro.disconnect();
  }, [children, fit, minPx, className, style]);

  const anchor = align ?? inherited;

  return (
    <div
      ref={box}
      className={className}
      style={{
        ...style,
        // FLEX, not the plain block this started as, and it is load-bearing for
        // the right- and centre-aligned lanes.
        //
        // An over-long `inline-block` in a `text-align: right` block still
        // STARTS at the content-box's left edge and spills past the right one —
        // `text-align` cannot pull back a box that does not fit. Its own right
        // edge was therefore already outside the cell, and scaling about
        // `right center` shrank it toward a point that was outside too: the
        // boarding pass's arrival field painted 86px past its cell and clean off
        // the card. Measured, not reasoned — it is why this is a flex box.
        //
        // With `justify-content: flex-end` an overflowing item hangs off the
        // START edge instead, so its right edge sits ON the cell's right edge
        // and the scale pulls it in from there. Same argument for `center`.
        display: "flex",
        justifyContent:
          anchor === "right"
            ? "flex-end"
            : anchor === "center"
              ? "center"
              : "flex-start",
      }}
    >
      <span
        ref={text}
        // Both classes are belt-and-braces, and saying so beats the two
        // confident reasons that stood here and were measured false.
        //
        // `inline-block` does nothing on its own: a flex item is BLOCKIFIED
        // (CSS Display 3 §2.7), and `getComputedStyle(span).display` really
        // does report `block`. The span has a width of its own because it is a
        // content-sized flex item, not because of this class.
        //
        // `flex-none` is not load-bearing either TODAY: the inherited
        // `white-space: nowrap` makes the automatic minimum size equal
        // max-content, so the item cannot be squeezed even at the default
        // `flex: 0 1 auto` (measured: `scrollWidth` 177 in a 120px box either
        // way). It becomes load-bearing the moment a lane drops `nowrap` —
        // a squeezed span reports `scrollWidth === clientWidth` and this
        // component would silently do nothing at all. Kept for that, not for
        // the reason first written here.
        className="inline-block flex-none"
        style={
          scale === 1
            ? undefined
            : {
                transform: `scale(${scale})`,
                // `bottom`, not `center`. Scaling about the vertical middle
                // lifts the box bottom by (1-s)·h/2 — measured at 6.6px for a
                // `text-3xl` value at s=0.62 — so a shrunk value floated off
                // the baseline its unshrunk neighbour still sits on. The
                // boarding pass shows it worst (`items-end`, two halves, one
                // shrunk), and every label/value pair shows it a little.
                // PassKit shrinks type inside a fixed box; it does not lift it.
                // A value centred in a band of its own has no neighbour's
                // baseline to keep — there the middle stays put (`originY`).
                transformOrigin: `${
                  anchor === "right"
                    ? "right"
                    : anchor === "center"
                      ? "center"
                      : "left"
                } ${originY}`,
              }
        }
      >
        {children}
      </span>
    </div>
  );
}

type PassCardProps = {
  bg: string;
  children: React.ReactNode;
  /**
   * Let the card be as tall as its content. Off for every Apple lane — see the
   * note below. On for the Google card, whose height is not fixed: its hero
   * image is drawn at full width in its own aspect, so the card grows with it.
   */
  autoHeight?: boolean;
};

export function PassCard({ bg, children, autoHeight = false }: PassCardProps) {
  // FIXED height, not min-height, and a column that lets the barcode sink to
  // the bottom. A pass on the device has a fixed face with the code anchored
  // low, so a sparse pass shows a large gap above it — the gap the customer
  // saw and the old auto-height preview never did, because it just shrank the
  // card to fit whatever was configured.
  return (
    <div
      className={`w-[320px] ${
        autoHeight ? "" : "h-[500px] "
      }rounded-2xl shadow-lg overflow-hidden flex flex-col`}
      style={{ backgroundColor: bg }}
    >
      {children}
    </div>
  );
}

type PassHeaderProps = {
  config: WalletConfig;
  currentLanguage: string;
  colors: PreviewColors;
};

export function PassHeader({
  config,
  currentLanguage,
  colors,
}: PassHeaderProps) {
  const t = useTranslations("walletBuilder");
  const visible = useVisibleCounts(config);
  const deviceOnly = useDeviceOnly();
  const certaintyOfHeader = useRowCertainty("headerFields");
  const { render, resolve, fieldText } = useTemplateRenderer();
  const samples = useWalletSamples();
  // Apple scales the logo to a fixed height and keeps its aspect, so its width
  // is a property of the FILE, not of the config — it can only be known once
  // the image has loaded. Zero until then, which is also the right answer for
  // a URL that never resolves: no logo on the device either.
  const [logoPt, setLogoPt] = React.useState(0);
  React.useEffect(() => setLogoPt(0), [config.logoUrl]);

  const brand = brandLineTemplate(config, currentLanguage, samples.primaryLang);
  // The length that SHIPS, not the length of the template: "{{companyName}}"
  // is 15 characters and resolves to 24 — and a line falling back to the
  // organisation name is capped by the renderer at 24, which this used to
  // ignore. It therefore drew more text than the device does and measured the
  // collision against a string nobody would ever see.
  const brandResolved = resolve(brand.text);
  const brandText = capBrandLine(brandResolved, brand.isFallback);
  const brandWasCapped = brandText !== brandResolved;
  const headerFields = (config.headerFields ?? []).slice(0, visible.header);
  const collides =
    headerFields.length > 0 &&
    logoPt +
      textPt(brandText) +
      headerFieldsPt(headerFields, currentLanguage, fieldText, resolve) >
      HEADER_ROW_PT;

  return (
    <div
      className="flex flex-col gap-1"
      // Measured off the same device shot: the header band is 66.9pt tall, and
      // it is NOT symmetric — 12.2pt above the label, 25.4pt below the value
      // before the strip begins. `pt-4 pb-2` had that backwards (18.8pt over
      // 9.4pt), which is most of why the preview's band read as too shallow
      // while its content sat too high in it.
      style={{
        paddingLeft: `${PASS_INSET_PX}px`,
        paddingRight: `${PASS_INSET_PX}px`,
        paddingTop: `${ptToPx(12)}px`,
        paddingBottom: `${ptToPx(25)}px`,
      }}
    >
      {/* Drawn the way PassKit draws it: one row, nothing wrapped, nothing
          shortened, and the header fields pinned right ON TOP of whatever the
          brand line runs into. The previous version gave the brand line a CSS
          ellipsis — a courtesy the device does not extend — so the preview
          showed a tidy "REICHERT+ COMMUNI…" for a layout that collided. */}
      {/* `min-h` sized to the STACK PINNED INSIDE IT, not to the brand line.
          The header fields are `absolute`, so they contribute nothing to this
          row's height — at 28px the taller value pushed them straight through
          the bottom and over the collision warning underneath, which is the
          one element that must stay readable. Derived rather than typed:
          label line + value line at the leading set above. */}
      <div
        className="relative flex items-center"
        style={{ minHeight: `${HEADER_STACK_PX}px` }}
      >
        <div className="flex items-center gap-2 whitespace-nowrap">
          {config.logoUrl && (
            <img
              src={config.logoUrl}
              alt="Logo"
              // Geometry from the SAME constants the collision model uses, so
              // the warning and the picture cannot disagree.
              style={{
                height: `${ptToPx(LOGO_HEIGHT_PT)}px`,
                maxWidth: `${ptToPx(LOGO_MAX_PT)}px`,
              }}
              className="object-contain"
              onLoad={(e) => {
                const el = e.currentTarget;
                if (!el.naturalHeight) return;
                setLogoPt(
                  Math.min(
                    LOGO_MAX_PT,
                    (el.naturalWidth / el.naturalHeight) * LOGO_HEIGHT_PT,
                  ),
                );
              }}
              onError={() => setLogoPt(0)}
            />
          )}
          {/* Apple draws `logoText` on the face (not organizationName, which is
              metadata). Falls back to organizationName so the brand still shows;
              the backend emits the same fallback into pass.json (logoText). */}
          {/* Once the fallback is capped there is no template left to chip —
              the device draws the trimmed string, so the preview does too. */}
          {/* `foregroundColor`, not `labelColor` — MEASURED 2026-09-03, and
              this reverses what stood here before.

              The previous note read the brand line's colour off a customer's
              screenshot by eye and concluded it matched the header LABEL. It
              asked, in as many words, for someone to confirm that by setting
              `labelColor` to magenta. Sampling the pixels answers it without
              the round trip, because the two candidate colours are far apart
              on that pass: the labels ("DATUM", "GAST", "PLATZ") are blue ink,
              mean RGB [44, 67, 171], and the values are near-black navy, mean
              RGB [21, 27, 45]. The brand line "Colora" measures [18, 24, 42].
              It is the VALUE colour, to within the antialiasing.

              Cross-check on the same shot, in case one sample landed wrong: a
              mask for blue ink over the whole pass face returns three bands —
              the header label, the secondary labels, and the auxiliary label.
              The brand line is not among them. On the cockpit's own screenshot
              the equivalent mask DOES return it, which is the bug.

              The fallback moves with it, from #000000 to `colors.fg`. Those
              agree today (`useColors` also falls back to #000000), so no
              template that never set a foreground colour changes; the point is
              that the brand line now follows one source instead of carrying a
              private copy that could drift from it. */}
          <div
            className="font-bold"
            style={{ color: colors.fg, fontSize: `${BRAND_LINE_PX}px` }}
          >
            {!brand.text
              ? // A pass with no logoText draws NO brand line. "Organization"
                // is a builder affordance naming the empty slot; the device
                // has nothing there at all.
                deviceOnly
                ? null
                : "Organization"
              : brandWasCapped
                ? brandText
                : render(brand.text)}
          </div>
        </div>
        {/* Pinned right and drawn ON TOP, because that is the order the device
            uses: the header field keeps its place and the brand line runs
            underneath it. z-10 keeps the field readable, as it is on the
            screenshot this was rebuilt from. */}
        <div className="absolute right-0 top-0 z-10 max-w-[70%] text-right">
          <FieldRow
            fields={config.headerFields}
            certaintyOf={certaintyOfHeader}
            currentLanguage={currentLanguage}
            colors={colors}
            visibleCount={visible.header}
            valuePx={HEADER_VALUE_PX}
            labelClassName="text-[10px] leading-none"
            // The container is `absolute right-0 … max-w-[70%]`, i.e. shrink to
            // fit, so it is already exactly as wide as its content and there is
            // normally no free space for `justify-content` to place. Measured
            // with one, two and three header fields: they pack against the
            // card's right edge either way, 13.6px from it — exactly
            // `PASS_INSET_PX`. `end` is set for the residual case (a last cell
            // at its `max-w` cap plus gaps that cannot be absorbed), where it
            // decides which end the few-pixel overflow falls off. Not the
            // load-bearing thing an earlier version of this comment claimed.
            justify="end"

            // The HEADER keeps its right pin until somebody measures a device.
            //
            // `textAlignment` is not the rare imported-template case it first
            // looked like: `WalletFieldEditor.addField` stamps "LEFT" on every
            // field an operator creates, and the shipped presets stamp all of
            // theirs. So honouring it here would move the header fields of
            // essentially EVERY existing template off the pass's right edge, in
            // one release, on the strength of a guess about how PassKit lays out
            // its header box against an explicit per-field alignment.
            //
            // The other sections have neutral containers, so there the key can
            // only make the preview more like the pass. Here it fights a
            // deliberate right pin rebuilt from a real screenshot. Measuring it
            // is one item on the device checklist; guessing it is a visible
            // change to every customer's builder.
            honourAlignment={false}
          />
        </div>
      </div>
      {/* Shown in BOTH lanes, and that is deliberate even though every other
          piece of builder chrome goes quiet in the 1:1 view.

          The warning is about a length, and the two views disagree about which
          length is on the card: the design lane draws `{{companyName}}` where
          the device draws "REICHERT+ COMMUNICATIONS". Suppressing it here left
          the warning visible only in the lane whose text does NOT collide, and
          invisible in the lane that demonstrates the collision — so no view
          ever showed the warning next to its own evidence. A warning that
          hides from its proof is worse than the rule it breaks.

          WHAT IT COSTS, and it is not nothing: this is drawn INSIDE the card,
          so it pushes the strip, every field row and the barcode down by its
          own height — roughly three lines in a 500px card. The gate that used
          to sit here said exactly that as its reason for hiding it, and that
          reason was sound; it just lost to the one above. An operator judging
          fidelity on a colliding template reads a card whose lower half sits
          too low. Fixing the collision is what restores it. */}
      {collides && (
        <div
          data-testid="header-collision-warning"
          className="body-3-regular font-error"
        >
          {t("headerRowOverflows")}
        </div>
      )}
    </div>
  );
}

/**
 * The header row's geometry, in pass points.
 *
 * <p>Three things share this row and PassKit wraps none of them: the logo
 * IMAGE, the brand line, and the header fields. The previous rule measured
 * only the brand line's character count, which is why it stayed silent for
 * "VELTRIC 0A" — ten characters — sitting on a device screenshot visibly
 * overlapping "2. Juli 2026". A wide logo collides with a short brand line
 * just as surely as a long brand line collides on its own.
 *
 * Calibrated against three device observations, two collisions and one near
 * miss:
 *   · COLLIDES — a 24-character brand line with NO logo against "July 2, 2026"
 *     (the customer's delivered pass)
 *   · COLLIDES — a 160pt logo plus a 10-character brand line against
 *     "2. Juli 2026" (signed test pass, measured off the screenshot)
 *   · CLEARS — the same 160pt logo with a 7-character brand line against the
 *     same date: 20px of gap at 3x, about 6.7pt, no overlap
 * The first two need a per-character width above ~9.53pt; the third caps it
 * below ~9.7. 9.6 sits inside that window, which is narrow enough that the
 * model is now pinned from BOTH sides rather than just the colliding one.
 *
 * Still a heuristic, and it cannot know that "REICHERT+ COMMUNICATIONS" is all
 * caps and therefore wider than the average it is measured against. Erring
 * towards warning is deliberate: a needless warning costs an operator a second
 * look, a missing one costs a customer their brand line.
 */
const PASS_WIDTH_PT = 375;
/**
 * The card's drawn width, and the bridge between the two systems in this file.
 *
 * Everything below models the DEVICE in points; everything in the JSX draws in
 * CSS pixels on a 320px card (`PassCard`'s `w-[320px]`). Nothing connected them,
 * so the logo was modelled at 50x160pt and drawn at 25x80px — a little under
 * HALF, once the 320/375 ratio is taken out. The collision warning was therefore
 * computed for a logo twice the size of the one beside it: it fired while the
 * picture plainly had room, which reads as a broken warning and trains an
 * operator to ignore the one that matters.
 */
const PREVIEW_WIDTH_PX = 320;
/** Points -> the pixels this preview draws them at. */
export const ptToPx = (pt: number): number => (pt * PREVIEW_WIDTH_PX) / PASS_WIDTH_PT;
const PASS_MARGIN_PT = 16;
const HEADER_ROW_PT = PASS_WIDTH_PT - 2 * PASS_MARGIN_PT;
const CHAR_PT = 9.6;
/** Apple draws the logo at a fixed height and will not let it past this width. */
const LOGO_HEIGHT_PT = 50;
const LOGO_MAX_PT = 160;
/**
 * Labels are set smaller than values, so they claim less of the row.
 *
 * 0.56 = 11.4pt / 20.4pt, both measured off the device on 2026-09-03 (see the
 * type-size block below). The 0.7 that stood here was the PREVIEW's own 10/14
 * ratio — the model was describing the picture it was supposed to be checking,
 * and over-claimed row width by a quarter for any field whose label is longer
 * than its value.
 */
const LABEL_PT_FACTOR = 0.56;
/** Gap PassKit leaves between two header fields. */
const FIELD_GAP_PT = 8;

/**
 * The two spacings above, in the pixels the JSX actually draws.
 *
 * Same bridge the logo already crosses at its `style={{ height: ptToPx(...) }}`
 * — extended to the field lane, which was the last place still spending raw
 * Tailwind units inside a card that models a device. `px-4` is 16px = 18.75pt
 * against Apple's 16pt, and `gap-x-4` is 16px = 18.75pt against Apple's 8pt, so
 * a two-field row had ~16% less room for text than the device gives it. That
 * deficit is what turned a value that FITS on the phone into one the preview
 * drew with an ellipsis.
 *
 * Inline styles rather than Tailwind arbitrary values on purpose: a literal
 * like `px-[13.65px]` is the same magic number the model exists to remove, and
 * a CSS variable would be empty for the primitives that tests mount on their
 * own.
 */
export const PASS_INSET_PX = ptToPx(PASS_MARGIN_PT);
export const FIELD_GAP_PX = ptToPx(FIELD_GAP_PT);

/**
 * Apple's side margin, ready to spread onto any block inside the card.
 *
 * Confirmed against the device shot rather than assumed: on that pass every
 * text run starts between 15.3pt and 17.1pt from the left edge and ends
 * between 359.0pt and 359.3pt on a 375pt face — i.e. a 16pt inset on both
 * sides, which is exactly PASS_MARGIN_PT.
 */
export const passInsetX = {
  paddingLeft: `${PASS_INSET_PX}px`,
  paddingRight: `${PASS_INSET_PX}px`,
} as const;

/**
 * Type sizes, in pass points, MEASURED off a device screenshot on 2026-09-03.
 *
 * Method, so the numbers can be re-derived rather than trusted: the pass face
 * was located by its background colour, giving px-per-point from the card's
 * known 375pt width (1077px on that shot). Ink was then isolated per row band
 * and the em size computed from the drawn cap/ascender height using the
 * cap-to-em ratio of the SAME text in the cockpit, whose CSS size is known.
 * Reading glyph heights alone is what makes this look like a 2x error; against
 * a known reference it is a quarter.
 *
 *   element   device     preview before   after
 *   label      11.4pt      11.7pt (10px)   unchanged - it already matched
 *   value      20.4pt      16.4pt (14px)   FIELD_VALUE_PT
 *   brand      19.1pt      14.1pt (12px)   BRAND_LINE_PT
 *
 * The label was left alone: the measurement puts it 0.3pt under what the
 * preview already draws, which is inside the error of reading a screenshot,
 * and moving it would drag the collision model's calibration with it for no
 * visible gain.
 *
 * Weights were measured too, by ink-per-bounding-box, and are already correct:
 * the brand line is bold on the device (0.376 against the preview's bold
 * 0.374) and the values are not (0.294/0.300 against the preview's medium
 * 0.317/0.287). The earlier reading that the preview had the two the wrong way
 * round does not survive the measurement.
 *
 * One value for header, secondary AND auxiliary, because that is what the
 * device draws: the header date (16.0pt cap), the guest name (15.3pt) and the
 * lone seat number (15.0pt) all come out the same size. The `text-xs` the
 * auxiliary rows used to ask for was a size the device has nowhere.
 */
const FIELD_VALUE_PT = 20;
/**
 * The header's value is set slightly larger than the other rows' — 21pt to
 * their 20.
 *
 * Small enough that it would be tempting to call it one size, and it is not.
 * Confirmed by rendering this card in a real browser and measuring it the same
 * way the device shot was measured: at a single shared 20pt the header value
 * came out 10.16pt per character against the device's 10.66 (+4.9%) while the
 * secondary value landed at 9.45 against 9.27 (-1.9%). One size cannot satisfy
 * both; two measurements say the header is the bigger one.
 */
const HEADER_VALUE_PT = 21;
/**
 * Label line + value line, the height the pinned header stack occupies.
 *
 * ONE line of each, which is only true because the row is `flex-nowrap`. The
 * stack is positioned absolutely, so it contributes nothing to the height of
 * the row it sits in; if it ever wraps again it grows through the bottom of
 * that row and paints over the collision warning and the strip. Measured at
 * 30.6px against the 33px this yields.
 *
 * HEADER_LABEL_PX is a hand-copy of the `text-[10px]` that `PassHeader` passes
 * as `labelClassName`, and nothing links the two. Change one and this guard
 * silently mis-sizes — the pair is the weak joint in it.
 */
const HEADER_LABEL_PX = 10;
const HEADER_LINE = 1.15;
const BRAND_LINE_PT = 19;
export const FIELD_VALUE_PX = ptToPx(FIELD_VALUE_PT);
export const HEADER_VALUE_PX = ptToPx(HEADER_VALUE_PT);
const HEADER_STACK_PX =
  Math.ceil((HEADER_LABEL_PX + HEADER_VALUE_PX) * HEADER_LINE);
export const BRAND_LINE_PX = ptToPx(BRAND_LINE_PT);

/**
 * The primary field PassKit draws across a strip image, measured on the same
 * shot: the ink spans 49.4pt of ascender height and 239.2pt of width for eight
 * lowercase characters, which puts the em at roughly 65pt — nearly half the
 * strip's own 99.2pt height. `text-3xl` (30px = 35pt) drew it at just over
 * half that.
 *
 * Two further facts from the same pixels, both of which the preview had wrong:
 *
 * WHITE, not `foregroundColor`. The glyphs measure 255/255/255 while every
 * other value on that pass is near-black navy [21, 27, 45]. PassKit does not
 * put the pass's foreground colour on the artwork.
 *
 * NO drop shadow. Sampling rings outward from the glyph edges gives 181 at
 * 0-2px (the glyph's own antialiasing), then 160.0 / 160.6 / 161.1 at 2-4,
 * 4-7 and 7-12px against 159.0 for untouched photo 18-26px away. A shadow
 * would darken the near rings; these are flat. The preview's
 * `text-shadow: 0 1px 2px rgba(0,0,0,.55)` is a legibility aid the device
 * does not use, and it was making the preview's text look heavier as well as
 * smaller.
 *
 * Weight is the one thing this measurement only bounds: ink-per-box comes out
 * at 0.253 against 0.376 for the bold brand line and 0.294 for a medium value.
 * The ascender whitespace in "asdfasdf" deflates it, so the honest reading is
 * "clearly not bold" rather than an exact weight. Normal is chosen; medium
 * would be within the error.
 */
const STRIP_PRIMARY_PT = 65;
export const STRIP_PRIMARY_PX = ptToPx(STRIP_PRIMARY_PT);

function textPt(text: string): number {
  return text.length * CHAR_PT;
}

function headerFieldsPt(
  fields: WalletField[],
  lang: string,
  fieldText: (f: WalletField, lang: string) => string,
  resolve: (text: string) => string,
): number {
  if (fields.length === 0) return 0;
  const widths = fields.map((f) => {
    const value = textPt(fieldText(f, lang));
    // No `?? f.key`: the renderer ships an empty label, so a keyless field
    // claims only its value's width.
    const label = textPt(resolve(f.label?.[lang] ?? "")) * LABEL_PT_FACTOR;
    return Math.max(value, label);
  });
  return (
    widths.reduce((a, b) => a + b, 0) + FIELD_GAP_PT * (fields.length - 1)
  );
}


type IntlStyle = "short" | "medium" | "long" | "full";

function intlStyle(style: string | undefined): IntlStyle | undefined {
  return style === "short" || style === "medium" || style === "long" || style === "full"
    ? style
    : undefined;
}

/**
 * What the field will actually read on the device.
 *
 * When the operator hands a date to Apple, the device formats it in the
 * GUEST's locale — so the preview has to do the same thing rather than keep
 * showing the string we would otherwise have baked in. Both the drawn text and
 * the collision width come through here, so the warning measures what is drawn.
 */
function fieldSampleText(
  field: WalletField,
  lang: string,
  samples: WalletSamples,
  deviceFormatsDates = true,
): string {
  const raw = resolveLangValue(field.value, lang, samples.primaryLang, "");
  // The SAME two conditions the pass builder applies, deliberately read from
  // the shared module rather than restated here. Restating them is how the
  // preview came to draw a formatted date for a field that ships prose — and
  // how "none/none" was right for the wrong reason.
  const draws =
    dateStyleDrawsSomething(field.dateStyle) ||
    dateStyleDrawsSomething(field.timeStyle);
  if (!deviceFormatsDates || !draws || !isSingleDateToken(raw)) {
    return resolveSampleText(raw, samples);
  }
  const date = intlStyle(field.dateStyle);
  const time = intlStyle(field.timeStyle);
  // Which end of the event the token stands for — the pass builder hands
  // `{{eventEndDate}}`/`{{eventEndTime}}` the END instant, and a preview that
  // showed the start for both would misdate every closing time.
  const trimmed = raw.trim();
  const instant =
    trimmed === "{{eventEndDate}}" || trimmed === "{{eventEndTime}}"
      ? samples.endInstant
      : samples.instant;
  // No instant behind the token for THIS event — the renderer bails the same
  // way (`instantForToken` returns null) and the field ships its text or is
  // dropped. Inventing one here is worse than showing the text.
  if (instant === null) return resolveSampleText(raw, samples);
  try {
    return new Intl.DateTimeFormat(lang || "en", {
      dateStyle: date,
      timeStyle: time,
      ...(samples.timeZone ? { timeZone: samples.timeZone } : {}),
    }).format(instant);
  } catch {
    // An unknown language tag must not take the preview down with it.
    return resolveSampleText(raw, samples);
  }
}

/** The text as it will ship: tokens replaced by their sample values. */
function resolveSampleText(text: string, samples: WalletSamples): string {
  return text.replace(TOKEN_RE, (token) => {
    const m = token.match(/^\{\{([^}]+)\}\}$/);
    if (!m) return token;
    const resolvable = rendererName(m[1]);
    if (resolvable === null) return "";
    return sampleForToken(resolvable, samples) ?? "";
  });
}

type StripWithPrimaryOverlayProps = {
  url?: string;
  fields: WalletField[] | undefined;
  currentLanguage: string;
  colors: PreviewColors;
  /** CSS aspect-ratio for the band — see `getStripAspect`. */
  aspect: string;
  /** Whether the device draws the field's LABEL over the strip. */
  showLabel?: boolean;
  /** The legibility scrim PassKit puts behind bottom-anchored text. */
  gradient?: boolean;
  /** Where the field sits in the band. */
  align?: "bottom" | "center";
  valueClassName?: string;
  /**
   * Drawn size in px, where one is measured. Only the event ticket has one —
   * see STRIP_PRIMARY_PT. Coupon and store card keep their class-driven size
   * because nobody has measured a device for those two, and inheriting the
   * event ticket's 65pt onto a band half again as tall would be a guess
   * dressed as a fix.
   */
  valuePx?: number;
  /** How many the device draws; the rest are dimmed and tagged, not hidden —
   *  same contract as `FieldRow`, so the builder never makes a customer's
   *  existing field vanish without explanation. */
  visibleCount?: number;
  /** Same contract as `FieldRow.certaintyOf` — the primary group's share. */
  certaintyOf?: (field: WalletField, index: number) => RowCertainty | undefined;
};

/**
 * Apple PassKit reality: when a strip image is present, the PRIMARY field is
 * drawn ON TOP of it, not in a row below.
 *
 * <p>eventTicket briefly did draw it below here. That was wrong, it was known
 * to be wrong (the commit that introduced the overlay says so), and it was
 * reintroduced three hours later with a comment asserting the opposite. The
 * customer's device settled it: the primary value sits on the artwork. The
 * variants below exist because the two families differ in the details — an
 * event ticket shows the value alone, big and centred in a 375×98 band; a
 * coupon or store card labels it and anchors it to the bottom of a taller one.
 */
export function StripWithPrimaryOverlay({
  url,
  fields,
  currentLanguage,
  colors,
  aspect,
  showLabel = true,
  gradient = true,
  align = "bottom",
  valueClassName = "text-xl font-bold",
  valuePx,
  visibleCount,
  certaintyOf,
}: StripWithPrimaryOverlayProps) {
  const { render, resolve, fieldText, text } = useTemplateRenderer();
  const deviceOnly = useDeviceOnly();
  const hasFields = !!fields && fields.length > 0;
  const anchor =
    align === "center"
      ? "inset-0 items-center"
      : "inset-x-0 bottom-0 pb-2 items-end";
  // Read HERE, from the prop: inside the field loop below `align` is
  // re-declared as the field's own `textAlignment`, which says nothing about
  // where in the band the lane sits.
  const shrinkOriginY = align === "center" ? "center" : "bottom";
  return (
    <div
      // `shrink-0`, because `PassCard` is a FIXED-height flex column
      // (`h-[500px] flex flex-col`) and flex items shrink by default. On a
      // template with enough fields the strip was compressed below its
      // `aspectRatio` — so the one element whose whole job is to show the
      // operator the crop they will get showed a crop that does not exist.
      // Apple gives the strip its aspect ratio and lays the fields out around
      // it; the preview has to do the same or the image hint under it is
      // measuring a band nobody will see.
      className="relative w-full shrink-0 overflow-hidden"
      style={{ aspectRatio: aspect }}
      data-testid="wallet-strip"
    >
      {url ? (
        <img
          src={url}
          alt="Strip"
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : deviceOnly ? (
        // A pass with no strip has no band at all — PassKit lays the fields
        // out as ordinary rows. The grey box naming the empty slot is an
        // upload affordance; leaving it in the device view would be showing
        // the operator a band their guests never get.
        null
      ) : (
        <div className="absolute inset-0 surface-tertiary flex items-center justify-center">
          <span className="body-3-regular font-tertiary">Strip Image Area</span>
        </div>
      )}
      {hasFields && (
        <>
          {gradient && (
            <div className="absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-black/45 to-transparent" />
          )}
          <div
            // `flex-nowrap` for the same reason as `FieldRow`: PassKit does
            // not wrap. The band has a fixed height, so a wrapped second line
            // would be clipped by the strip rather than moving anything.
            className={`absolute ${anchor} flex flex-row flex-nowrap`}
            style={{ ...passInsetX, columnGap: `${FIELD_GAP_PX}px` }}
            data-testid="wallet-strip-overlay"
          >
            {fields!.map((field, i) => {
              const value = text(field.value, currentLanguage);
              // `alignOf` here TOO. This overlay is the primary-field lane for
              // eventTicket, coupon and storeCard whenever a strip image is
              // set, and it draws its own cells rather than going through
              // `FieldRow` — so teaching only `FieldRow` about `textAlignment`
              // left the same field centred without a strip and left-aligned
              // with one, while the device centres it either way. Uniformly
              // wrong was at least reportable; inconsistently wrong is not.
              const align = alignOf(field.textAlignment);
              // The date-style path applies here exactly as it does in a
              // FieldRow — a primary field drawn on the strip is still a field,
              // and an event ticket's date can live in it.
              const formatted = fieldText(field, currentLanguage);
              const appleFormats = formatted !== resolve(value ?? "");
              const beyond = visibleCount !== undefined && i >= visibleCount;
              if (beyond && deviceOnly) return null;
              // The renderer's drop rule applies HERE too, and teaching it only
              // to `FieldRow` left the reported bug alive in the one lane that
              // does not go through it. `showLabel` defaults true, so on a
              // coupon or store card with artwork a primary field whose value
              // resolves to nothing drew its LABEL across the photo with
              // nothing under it — an orphaned caption on the view whose whole
              // claim is that it draws what the device draws.
              if (
                deviceOnly &&
                rendererDropsRow(
                  resolve(value ?? ""),
                  value,
                  resolve(text(field.label, currentLanguage) ?? ""),
                )
              ) {
                return null;
              }
              // The same answer `FieldRow` gives, from the same function: a row
              // the client could not decide is not the orphaned caption the
              // rule above deletes.
              //
              // `showLabel` decides whether the AWAITED half is drawable at
              // all. This lane puts the value ON the artwork, and the event
              // ticket turns the label off — a bar with no label beside it is
              // not "a value is coming", it is a grey rectangle on a photo. So
              // where there is no label there is no bar; the `absent` verdict
              // still deletes the cell, because that one is about existence
              // rather than about explaining anything.
              const rawMark = emptyValueMark({
                deviceOnly,
                certainty: certaintyOf?.(field, i),
                labelColor: colors.lbl,
              });
              const mark =
                !showLabel && rawMark !== HIDE_ROW && deviceOnly ? null : rawMark;
              if (!value && mark === HIDE_ROW) return null;
              return (
                <div
                  key={field.key || i}
                  // `flex-1`, or the alignment is decoration. The cell sits in
                  // a `flex-row flex-wrap` with default `flex: 0 1 auto`, so it
                  // is content-sized — `text-align` then has nothing to move
                  // the text within, and a CENTER field still drew hard left.
                  // Every shipped preset has exactly one primary field here, so
                  // filling the row is also what it should have been doing.
                  style={align ? { textAlign: align } : undefined}
                  // No `text-shadow`: measured absent on the device — the rings
                  // around the glyphs are the photo's own luminance (see
                  // STRIP_PRIMARY_PT). It was ours, not PassKit's.
                  className={`flex-1 min-w-0 ${beyond ? "opacity-40" : ""}`}
                  {...(beyond
                    ? { "data-beyond-limit": "true", "data-field-key": field.key }
                    : {})}
                >
                  {showLabel && (
                    <div
                      className="text-[10px] uppercase tracking-wider truncate"
                      style={{ color: colors.lbl }}
                    >
                      {text(field.label, currentLanguage)
                        ? render(text(field.label, currentLanguage))
                        : "\u00a0"}
                    </div>
                  )}
                  {/* WHITE, not `colors.fg`. Measured: on a pass whose every
                      other value is near-black navy [21,27,45], the text over
                      the artwork is 255/255/255. PassKit does not carry
                      `foregroundColor` onto a strip image.

                      Applied to this lane as a whole rather than to the event
                      ticket alone. The measurement is an event ticket, but the
                      mechanism is "text drawn on artwork", which is what all
                      three callers of this component are doing — and the two
                      unmeasured ones already ask for `gradient`, a dark scrim
                      that only makes sense under light text. One rule beats a
                      fork nobody can justify in either direction. Same reason
                      the SIZE is not shared: see `valuePx`. */}
                  {/* No `truncate` here either, and here it was not merely
                      dishonest — it made the field DISAPPEAR. `text-overflow`
                      cannot split the atomic `inline-flex` chip a merge field
                      renders as, so once the value outgrew the band the
                      browser dropped the whole chip and drew an ellipsis in
                      white on the artwork. The builder showed bare artwork for
                      a pass that ships its event name across it. */}
                  <FitText
                    // The strip primary is the WORST case in the whole card and
                    // the one the report was about: it is the largest type on
                    // the pass, it sits in a band only as wide as the card, and
                    // in the design lane it draws `{{eventName}}` — a merge
                    // field far longer than the value it stands for. It ran off
                    // the artwork mid-glyph.
                    //
                    // No `align` prop: the cell above already carries this
                    // field's stored `textAlignment` as `text-align`, and
                    // `FitText` inherits it. Hard-coding `left` here made a
                    // CENTER primary sit flush left ON the artwork while the
                    // identical field centred on a strip-less card — and the
                    // test guarding it could not see the difference, because it
                    // asserts the cell's `text-align`, which is exactly the
                    // property a flex item stops obeying.
                    // Centred lane (event ticket): a long title shrinks about
                    // the band's middle, where the device keeps it — the
                    // bottom anchor dropped it below a centred motif that the
                    // delivered pass draws it straight across. The bottom lane
                    // (coupon, store card) keeps its bottom edge, as before.
                    originY={shrinkOriginY}
                    className={`${valueClassName} whitespace-nowrap overflow-hidden`}
                    style={{
                      // White where there is ARTWORK, which is what was
                      // measured: text on a photo. With no artwork there is
                      // nothing for it to stay legible against, and it became
                      // white on the pass background — contrast 1.07:1 against
                      // the builder's placeholder, and flatly invisible in the
                      // device view, where the placeholder is gone.
                      color: url ? "#ffffff" : colors.fg,
                      ...(valuePx ? { fontSize: `${valuePx}px` } : {}),
                    }}
                  >
                    {!value ? (
                      (mark as React.ReactNode)
                    ) : appleFormats ? (
                      formatted
                    ) : (
                      render(value)
                    )}
                  </FitText>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

type ThumbnailImageProps = {
  url?: string;
  size?: number;
};

export function ThumbnailImage({ url, size = 80 }: ThumbnailImageProps) {
  const deviceOnly = useDeviceOnly();
  const dim = { width: size, height: size };
  // Same reasoning as the strip's: an empty slot draws nothing on the device,
  // and the fields beside it get the width back.
  if (!url && deviceOnly) return null;
  if (url) {
    return (
      <img
        src={url}
        alt="Thumbnail"
        className="object-cover rounded-md shrink-0"
        style={dim}
      />
    );
  }
  return (
    <div
      className="surface-tertiary rounded-md flex items-center justify-center shrink-0"
      style={dim}
    >
      <span className="text-[10px] font-tertiary text-center px-1">
        Thumbnail
      </span>
    </div>
  );
}

type BarcodeBlockProps = {
  config: WalletConfig;
  colors: PreviewColors;
};

export function BarcodeBlock({ config, colors }: BarcodeBlockProps) {
  const { render } = useTemplateRenderer();
  const type = config.barcodeType;
  // Mirror the render path: PassJsonBuilder emits a barcode only when BOTH the
  // type and the value are set. Drawing one regardless promised a code on
  // templates that ship without any — a plain info card is a legitimate pass.
  if (!type?.trim() || !config.barcodeValue?.trim()) return null;
  // Normalised, because the RENDERER normalises: `PassJsonBuilder.barcodeFormat`
  // switches on `type.toLowerCase(Locale.ROOT)`, and `configPatch` writes the
  // type lowercase. An exact comparison against "Code128" therefore drew a
  // SQUARE QR for a stored `code128` while the pass carried a wide linear
  // strip — the two shapes this block exists to tell apart.
  const norm = type.trim().toLowerCase();
  const isLinear = norm === "code128" || norm === "pdf417";
  return (
    <div
      className="pb-4 pt-2 mt-auto flex flex-col items-center gap-1"
      style={passInsetX}
    >
      {/* Real wallet barcodes always render as a solid white card with a black
          code (never the pass colors), so it stays scannable on any pass.
          Linear formats (Code128/PDF417) are wide strips, not squares. */}
      <div
        className={`bg-white rounded-lg flex flex-col items-center justify-center p-2 ${
          isLinear ? "w-56 h-20" : "w-40 h-40"
        }`}
      >
        {/* No type caption — a real wallet renders only the code (+ optional
            altText below), never a "QR" label under it. */}
        {isLinear ? (
          <IconBarcode className="w-[90%] h-12 text-black" />
        ) : (
          // Real (scannable) code so the preview reads like a finished pass —
          // the delivered pass encodes the guest's signed token, not this URL.
          // Aztec previews as QR too; close enough for a mock.
          <QRCodeSVG value="https://veltric.app" size={128} level="M" />
        )}
      </div>
      {config.barcodeAltText && (
        <div
          data-testid="wallet-barcode-alt"
          className="text-xs text-center"
          style={{ color: colors.lbl }}
        >
          {render(config.barcodeAltText)}
        </div>
      )}
    </div>
  );
}

// NOTE: there is intentionally no DescriptionBlock. Apple's `description` key is
// metadata only (add-sheet title + VoiceOver) and is NEVER drawn on the pass
// face for any passStyle, so no preview renders it.
